import type { ClientOption } from "api-types/options/optionsResponses";
import type {
  ClientOptionSet,
  ClientOptionSetHydrated,
  ClientPushToCatalogResponse,
} from "api-types/optionSets/optionSetsResponses";
import { MAX_REQUEST_TIMEOUT } from "common/constants";
import type { CSVColumnMapping, CSVColumnParsing } from "common/utils/csv";
import type { NotificationWithHTTPStatusCode } from "common/utils/fetchHelpers";
import { handleFetchError } from "common/utils/fetchHelpers";
import objectIsEmpty from "common/utils/objectIsEmpty";
import type { Modify } from "common/utils/types";
import {
  getOptionCatalogsMigrationURL,
  getOptionSetsCsvURL,
  getOptionSetsURL,
  getStudioDocOptionSetsURL,
} from "lib/endpointHelpers";
import type { FileUploadWithTimeoutParams } from "lib/utils/fetchAuthHelpers";
import {
  deleteWithTimeout,
  fileUploadWithTimeout,
  getWithTimeout,
  postWithTimeout,
  putWithTimeout,
} from "lib/utils/fetchAuthHelpers";
import type { StorageResponse } from "./types";

interface UploadOptionSetsCSVParams {
  builderId: string;
  file: File;
  columnParsing?: CSVColumnParsing;
  columnMapping?: CSVColumnMapping;
}
export type CatalogOptionUpdateData = Partial<Modify<ClientOption, { action: string }>>;
export type CatalogOptionSetUpdateData = Partial<
  Modify<
    ClientOptionSetHydrated,
    {
      options: CatalogOptionUpdateData[];
    }
  >
>;

export async function putOptionSetToCatalog(
  builderId: string,
  dbOptionSetId: string,
  dbOptionSet: CatalogOptionSetUpdateData,
): StorageResponse<ClientOptionSetHydrated> {
  try {
    const res: Response = await putWithTimeout(getOptionSetsURL(builderId, dbOptionSetId), dbOptionSet);
    if (!res.ok) {
      throw res;
    }

    const optionSet: ClientOptionSetHydrated = await res.json();

    return [optionSet, undefined];
  } catch (error) {
    console.error(error);
    return [
      ,
      {
        messageType: "error",
        message: "Error updating Option Set for Catalog",
      },
    ];
  }
}

export async function pushOptionSetToCatalog(
  builderId: string,
  optionSetId: string,
): StorageResponse<ClientOptionSetHydrated> {
  try {
    const res: Response = await postWithTimeout(`${getOptionSetsURL(builderId, optionSetId)}/push-to-catalog`);
    if (!res.ok) {
      throw res;
    }

    const optionSet: ClientOptionSetHydrated = await res.json();

    return [optionSet, undefined];
  } catch (error) {
    console.error(error);
    return [
      ,
      {
        messageType: "error",
        message: "Error promoting Option Set to Catalog",
      },
    ];
  }
}

export async function getStudioDocOptionSets(
  builderId: string,
  studioDocId: string,
): StorageResponse<ClientOptionSetHydrated[], null> {
  try {
    const res = await getWithTimeout(getStudioDocOptionSetsURL(builderId, studioDocId));

    if (!res.ok) {
      throw res;
    }

    const studioDocData = await res.json();

    return [studioDocData.data, null];
  } catch (e) {
    return [
      null,
      {
        messageType: "error",
        message: "Error getting Option Sets for Studio Doc",
      },
    ];
  }
}

export async function bulkPushToCatalog(
  builderId: string,
  studioDocIds: string[],
): StorageResponse<ClientPushToCatalogResponse> {
  try {
    const res: Response = await postWithTimeout(`${getOptionSetsURL(builderId)}/push-doc-options-to-catalog`, {
      studioDocIds: [...new Set(studioDocIds)],
    });
    if (!res.ok) {
      throw res;
    }

    const status: ClientPushToCatalogResponse = await res.json();

    return [status, undefined];
  } catch (error) {
    console.error(error);
    return [
      ,
      {
        messageType: "error",
        message: "Error pushing Doc Option Sets to Catalog",
      },
    ];
  }
}

export async function updateOptionSet(
  builderId: string,
  optionSetId: string,
  optionSetData: Partial<ClientOptionSet>,
): StorageResponse<ClientOptionSetHydrated> {
  try {
    const res: Response = await putWithTimeout(`${getOptionSetsURL(builderId, optionSetId)}`, optionSetData);
    if (!res.ok) {
      throw res;
    }

    const optionSet: ClientOptionSetHydrated = await res.json();

    return [optionSet, undefined];
  } catch (error) {
    console.error(error);
    return [
      ,
      {
        messageType: "error",
        message: "Error updating Option Set",
      },
    ];
  }
}

export async function deleteOptionSet(builderId: string, optionSetId: string): StorageResponse<string> {
  try {
    const res = await deleteWithTimeout(`${getOptionSetsURL(builderId, optionSetId)}`);
    if (!res.ok) {
      throw res;
    }

    return [optionSetId, undefined];
  } catch (error) {
    console.error(error);
    return [
      ,
      {
        messageType: "error",
        message: "Error deleting Option Set",
      },
    ];
  }
}

export async function uploadOptionSetsCSV({
  builderId,
  file,
  columnParsing,
  columnMapping,
}: UploadOptionSetsCSVParams): StorageResponse<ClientOption[]> {
  const params: FileUploadWithTimeoutParams = {
    path: getOptionSetsCsvURL(builderId),
    fileName: "csv",
    file,
    timeout: MAX_REQUEST_TIMEOUT,
  };

  params.body = {};

  if (!objectIsEmpty(columnMapping)) {
    params.body.columnMapping = columnMapping;
  }

  /**
   * For now we only want to apply the columnParsing to the groupIdentifier
   */
  if (!objectIsEmpty(columnParsing) && columnMapping?.groupIdentifier) {
    params.body.columnParsing = columnParsing;
  }

  try {
    const res = await fileUploadWithTimeout(params);

    if (!res.ok) {
      throw res;
    }

    const options: ClientOption[] = await res.json();

    return [options, undefined];
  } catch (error) {
    let errorMessage = "An error occurred while uploading your Options CSV";

    // TODO: address the fact that a Response object is thrown from fetchWithTimeout
    if (error instanceof Response && error.statusText) {
      errorMessage = error.statusText;
    }

    return [
      ,
      {
        messageType: "error",
        message: errorMessage,
      },
    ];
  }
}

export async function migrateStudioDocOptions(params: {
  builderId: string;
  studioDocId: string;
}): Promise<NotificationWithHTTPStatusCode | null> {
  try {
    const res = await postWithTimeout(
      `${getOptionCatalogsMigrationURL(params.builderId)}/migrate-options?studioDocId=${params.studioDocId}`,
      undefined,
      30 * 1000,
    );

    if (!res.ok) {
      throw res;
    }

    return null;
  } catch (e) {
    return await handleFetchError(e);
  }
}

export default {
  uploadOptionSetsCSV,
};
